import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  vehicleManagementService,
  MakeSummary,
  ModelSummary,
} from '../../api/services/vehicleManagementService';

export type ActiveLevel = 'makes' | 'models';

export const useVehicleManagement = () => {
  // ── Data ─────────────────────────────────────────────────────────────────
  const [makes, setMakes] = useState<MakeSummary[]>([]);
  const [models, setModels] = useState<ModelSummary[]>([]);

  // ── Selection breadcrumb ─────────────────────────────────────────────────
  const [selectedMake, setSelectedMake] = useState<string | null>(null);
  const [activeLevel, setActiveLevel] = useState<ActiveLevel>('makes');

  // ── Loading / action states ───────────────────────────────────────────────
  const [makesLoading, setMakesLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [addMakeOpen, setAddMakeOpen] = useState(false);
  const [addModelOpen, setAddModelOpen] = useState(false);

  // ── Fetchers ─────────────────────────────────────────────────────────────

  const fetchMakes = useCallback(async () => {
    setMakesLoading(true);
    try {
      const data = await vehicleManagementService.getMakes();
      setMakes(data);
    } catch {
      message.error('Failed to load makes');
    } finally {
      setMakesLoading(false);
    }
  }, []);

  const fetchModels = useCallback(async (make: string) => {
    setModelsLoading(true);
    try {
      const data = await vehicleManagementService.getModels(make);
      setModels(data);
    } catch {
      message.error('Failed to load models');
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // ── Navigation ────────────────────────────────────────────────────────────

  const drillIntoMake = (make: string) => {
    setSelectedMake(make);
    setActiveLevel('models');
    fetchModels(make);
  };

  const goBack = () => {
    if (activeLevel === 'models') {
      setActiveLevel('makes');
      setSelectedMake(null);
    }
  };

  // ── Make actions ─────────────────────────────────────────────────────────

  const handleCreateMake = async (name: string) => {
    setActionLoading(true);
    try {
      await vehicleManagementService.createMake(name);
      message.success(`Make "${name}" created`);
      setAddMakeOpen(false);
      await fetchMakes();
      drillIntoMake(name);
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Failed to create make');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteMake = async (name: string) => {
    setActionLoading(true);
    try {
      await vehicleManagementService.deleteMake(name);
      message.success(`Deleted make "${name}"`);
      fetchMakes();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Failed to delete make');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Model actions ─────────────────────────────────────────────────────────

  const handleCreateModel = async (model: string) => {
    if (!selectedMake) return;
    setActionLoading(true);
    try {
      await vehicleManagementService.createModel(selectedMake, model);
      message.success(`Model "${model}" created`);
      setAddModelOpen(false);
      fetchModels(selectedMake);
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Failed to create model');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteModel = async (model: string) => {
    if (!selectedMake) return;
    setActionLoading(true);
    try {
      await vehicleManagementService.deleteModel(selectedMake, model);
      message.success(`Deleted model "${model}"`);
      fetchModels(selectedMake);
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Failed to delete model');
    } finally {
      setActionLoading(false);
    }
  };

  return {
    // data
    makes, models,
    // selection
    selectedMake, activeLevel,
    // loading
    makesLoading, modelsLoading, actionLoading,
    // modal state
    addMakeOpen, setAddMakeOpen,
    addModelOpen, setAddModelOpen,
    // fetchers
    fetchMakes, fetchModels,
    // navigation
    drillIntoMake, goBack,
    // actions
    handleCreateMake, handleDeleteMake,
    handleCreateModel, handleDeleteModel,
  };
};
